import { useEffect, useMemo, useState } from "react";
import PortalLayout from "@/pages/portal/PortalLayout";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import {
  Package, Download, ExternalLink, Loader2, FolderOpen,
  Palette, FileText, Image, Type, Globe, Link2, RefreshCw,
} from "lucide-react";

const CAT_META = {
  logo:              { label: "Logos",             icon: Palette },
  label:             { label: "Label Artwork",     icon: Palette },
  brand_guidelines:  { label: "Brand Guidelines",  icon: FileText },
  color_palette:     { label: "Color Palette",     icon: Palette },
  fonts:             { label: "Fonts",             icon: Type },
  website:           { label: "Website",           icon: Globe },
  domain:            { label: "Domain",            icon: Link2 },
  packaging:         { label: "Packaging",         icon: Package },
  photography:       { label: "Photography",       icon: Image },
  other:             { label: "Other",             icon: FolderOpen },
};

const PortalVault = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const token = typeof window !== "undefined" ? localStorage.getItem("flowtalis_token") : null;

  const load = () => {
    setLoading(true);
    api.get("/portal/vault")
      .then((r) => setAssets(r.data.assets || []))
      .catch(() => setAssets([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const a of assets) {
      const k = a.category || "other";
      (g[k] = g[k] || []).push(a);
    }
    return g;
  }, [assets]);

  const downloadAll = async () => {
    setDownloading(true);
    try {
      const url = `${API}/portal/vault/download-all?auth=${encodeURIComponent(token || "")}`;
      // Trigger a browser download via anchor click
      const a = document.createElement("a");
      a.href = url;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("Preparing your ZIP…");
    } catch {
      toast.error("Failed to build ZIP");
    } finally {
      setTimeout(() => setDownloading(false), 1500);
    }
  };

  const fileAssets = assets.filter((a) => a.asset_type === "file").length;

  return (
    <PortalLayout>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="ft-eyebrow mb-2">Client Portal</div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="portal-vault-title">
            Brand Vault
          </h1>
          <p className="text-[var(--ft-muted)] mt-2 max-w-2xl">
            Your complete digital brand kit — logos, label artwork, brand guidelines,
            color palettes, fonts, and website details. Download any asset, or grab
            everything at once as a ZIP.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="ft-btn-ghost" data-testid="portal-vault-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            onClick={downloadAll}
            disabled={downloading || fileAssets === 0}
            className="ft-btn-primary"
            data-testid="portal-vault-download-all"
          >
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Download All Assets (ZIP)
          </button>
        </div>
      </div>

      {loading ? (
        <div className="ft-card fx-skeleton h-64" />
      ) : assets.length === 0 ? (
        <div className="ft-card text-center py-16" data-testid="portal-vault-empty">
          <Package size={40} className="mx-auto mb-4 text-[var(--ft-muted)]" />
          <h3 className="text-lg font-semibold">Your Brand Vault is being prepared.</h3>
          <p className="text-[var(--ft-muted)] mt-2 max-w-md mx-auto">
            As your FLOWTALIS design team ships approved artwork and brand assets,
            they&apos;ll land in this vault automatically. Check back soon.
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([cat, items]) => {
            const meta = CAT_META[cat] || CAT_META.other;
            const Icon = meta.icon;
            return (
              <div key={cat} data-testid={`portal-vault-cat-${cat}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon size={16} className="text-[var(--ft-gold)]" />
                  <h2 className="text-lg font-semibold tracking-tight">{meta.label}</h2>
                  <span className="text-xs text-[var(--ft-muted)]">({items.length})</span>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {items.map((a) => (
                    <VaultCard key={a.id} asset={a} token={token} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </PortalLayout>
  );
};

const VaultCard = ({ asset, token }) => {
  const isLink = asset.asset_type === "link";
  const downloadUrl = `${API}/portal/vault/${asset.id}/download?auth=${encodeURIComponent(token || "")}`;
  const size = asset.size ? `${(asset.size / 1024 / 1024).toFixed(2)} MB` : null;
  return (
    <div className="ft-card" data-testid={`portal-vault-asset-${asset.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-bold tracking-tight truncate">{asset.title}</h3>
          {asset.description && (
            <p className="text-xs text-[var(--ft-muted)] mt-1 line-clamp-2">{asset.description}</p>
          )}
          <div className="text-[0.65rem] text-[var(--ft-muted)] mt-2 uppercase tracking-[0.16em]">
            {isLink ? "External link" : `File · ${size || ""}`}
          </div>
        </div>
      </div>
      <div className="mt-4">
        {isLink ? (
          <a
            href={asset.external_url}
            target="_blank"
            rel="noreferrer"
            className="ft-btn-secondary inline-flex"
            data-testid={`portal-vault-open-${asset.id}`}
          >
            <ExternalLink size={14} /> Open Link
          </a>
        ) : (
          <a
            href={downloadUrl}
            download={asset.original_filename || asset.title}
            className="ft-btn-secondary inline-flex"
            data-testid={`portal-vault-download-${asset.id}`}
          >
            <Download size={14} /> Download
          </a>
        )}
      </div>
    </div>
  );
};

export default PortalVault;
